/**
 * Shared behaviour for transient layers (dialogs, sheets, popovers).
 *
 * Extracted from Modal so Sheet cannot drift from it. Scroll locking in particular
 * has to be reference-counted — two overlays open at once and a naive implementation
 * unlocks the page while one is still showing.
 *
 * No `"use client"`: these are plain functions, and keeping them out of a client
 * module means style/behaviour helpers stay importable from anywhere.
 */

import type { KeyboardEvent as ReactKeyboardEvent } from 'react';

export const FOCUSABLE_SELECTOR =
  'a[href], area[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Nested overlays each take a lock; the page scrolls again only when the last one
 * releases. The count lives on the document element so it survives across the
 * separate React trees that portalled overlays render into.
 */
export function lockDocumentScroll(): void {
  const root = document.documentElement;
  root.dataset.ssOverlayLocks = String(Number(root.dataset.ssOverlayLocks ?? '0') + 1);
  root.classList.add('ss-overlay-scroll-locked');
}

export function unlockDocumentScroll(): void {
  const root = document.documentElement;
  const remainingLocks = Math.max(0, Number(root.dataset.ssOverlayLocks ?? '1') - 1);
  if (remainingLocks === 0) {
    delete root.dataset.ssOverlayLocks;
    root.classList.remove('ss-overlay-scroll-locked');
    return;
  }
  root.dataset.ssOverlayLocks = String(remainingLocks);
}

/**
 * Cycles Tab within `container`. Returns true when the event was handled, so callers
 * can decide whether to run their own key handling afterwards.
 */
export function trapTabKey(
  event: KeyboardEvent | ReactKeyboardEvent,
  container: HTMLElement | null,
): boolean {
  if (event.key !== 'Tab' || !container) return false;
  const items = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
  if (items.length === 0) {
    event.preventDefault();
    container.focus();
    return true;
  }
  const first = items[0];
  const last = items[items.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
    return true;
  }
  if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
    return true;
  }
  return false;
}

/**
 * Moves focus into the overlay and hands it back to its connected opener on close.
 * An explicitly supplied fallback is evaluated only if that opener was removed or BODY.
 * Returns the teardown function.
 *
 * Focus is deferred a frame because the panel is not laid out on the tick it mounts,
 * and `preventScroll` on restore stops the page from jumping to the opener.
 */
export function captureFocus(
  container: HTMLElement | null,
  initialFocus?: HTMLElement | null,
  fallbackReturnFocus?: () => HTMLElement | null | undefined,
): () => void {
  const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  const frame = window.requestAnimationFrame(() => {
    const target = initialFocus
      ?? container?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR)
      ?? container;
    target?.focus();
  });
  return () => {
    window.cancelAnimationFrame(frame);
    const available = (target: HTMLElement | null | undefined): target is HTMLElement =>
      !!target && target !== document.body && target !== document.documentElement && target.isConnected;
    const target = available(opener) ? opener : fallbackReturnFocus?.();
    if (available(target)) target.focus({ preventScroll: true });
  };
}
